import HomeMediaFolder from './home_media_ui/Folder';
import MaterialsAccessPage from './home_media_ui/MaterialsAccessPage';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';
import { useContext, useEffect, useState } from 'react';
import { CssVarsProvider, extendTheme } from '@mui/joy';
import { AnimatePresence, motion } from 'framer-motion';
import { Box, Button, CircularProgress, Stack, Sheet, Typography } from '@mui/joy';

import LayoutContextProvider, { LayoutContext } from './context';
import { needsClientProfile } from './shared/clientSession';
import AuthModal from './components/modals/AuthModal';
import OrderModal from './components/modals/OrderModal';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';

import 'leaflet/dist/leaflet.css';

import HomePage from './content/HomePage';
import LKPage from './content/LKPage';
import MainPage from './content/MainPage';
import ClientChatModal from './components/modals/ClientChatModal';
import QuickOrderModal from './components/modals/QuickOrderModal';
import PushPrompt from './components/PushPrompt';
import NewsStoriesModal from './components/News/NewsStoriesModal';
import AccessPage from './content/LK/AccessPage';
import NotificationsPage from './content/LK/NotificationsPage';
import ProfilePage from './content/LK/ProfilePage';
import ReferralPage from './content/LK/ReferralPage';
import PaymentsPage from './content/LK/PaymentsPage';
import DocumentsPage from './content/LK/DocumentsPage';
import { initTracker, track, setTrackerPath, setTrackerEnabled } from './utils/tracker';



// ─── MUI Joy Theme ────────────────────────────────────────────────────────────
const theme = extendTheme({
  colorSchemes: {
    light: {
      palette: {
        primary: {
          50:  "#f2f0fd",
          100: "#e4ddfb",
          200: "#d4c8f9",
          300: "#b8a4f4",
          400: "#8d6aec",
          500: "#6366f1",
          600: "#4d26af",
          700: "#43229b",
          800: "#391d87",
          900: "#2f1973",
        },
      },
    },
  },
  components: {
    JoyButton:      { styleOverrides: { root: ({ theme }) => ({ transition: ".2s", borderRadius: theme.vars.radius.lg, "&:hover": { transform: "scale(1.02)" } }) } },
    JoyInput:       { styleOverrides: { root: ({ theme }) => ({ borderRadius: theme.vars.radius.lg }) } },
    JoySelect:      { styleOverrides: { root: ({ theme }) => ({ borderRadius: theme.vars.radius.lg }) } },
    JoyTextarea:    { styleOverrides: { root: ({ theme }) => ({ borderRadius: theme.vars.radius.lg }) } },
    JoyCard:        { styleOverrides: { root: { transition: "box-shadow .2s, transform .2s" } } },
    JoyModalDialog: { styleOverrides: { root: ({ theme }) => ({ borderRadius: theme.vars.radius["2xl"] }) } },
  },
});

// ─── Спиннер ──────────────────────────────────────────────────────────────────
function LoadingScreen() {
  return (
    <Stack justifyContent="center" alignItems="center" spacing={2} sx={{ height: "100vh" }}>
      <Sheet
        component={"img"}
        src='/logo.png'
        sx={{
          width: 38, height: 38
        }}
      />
      <CircularProgress size="sm" />
      <Typography level="body-sm" color="neutral">Загрузка...</Typography>
    </Stack>
  );
}

// ─── Роут только для авторизованных ──────────────────────────────────────────
const mediaReturnKey = 'home-media-return';
const isMediaRoute = path => /^\/lk\/storage\/[1-9]\d*\/(materials|input|measurement|sheets|photos|result)\/?$/.test(path || '');

function PrivateRoute({ children }) {
  const { account, loading, setModalAuth, permissions } = useContext(LayoutContext);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !account) {
      if (isMediaRoute(location.pathname)) {
        try { sessionStorage.setItem(mediaReturnKey, location.pathname); } catch { /* приватный режим */ }
      }
      setModalAuth(true);
      navigate("/", { replace: true });
    }
  }, [account, loading, location.pathname, navigate, setModalAuth]);

  // Редирект если дочерний аккаунт зашёл на страницу без доступа
  useEffect(() => {
    if (!account || loading) return;
    if (permissions === '__all__') return;
    if (!Array.isArray(permissions)) return;

    const path = location.pathname;

    // На /lk (список заявок) нужно view_application
    if (path === '/lk' && !permissions.includes('view_application')) {
      // Пробуем перекинуть на доступы
      if (permissions.some(p => ['add_access', 'edit_access', 'delete_access'].includes(p))) {
        navigate('/lk/access', { replace: true });
      } else {
        navigate('/lk', { replace: true });
      }
    }

    // На /lk/access нужно хотя бы одно из прав на доступы
    if (path === '/lk/access') {
      const accessPerms = ['add_access', 'edit_access', 'delete_access', 'edit_access_permissions'];
      if (!permissions.some(p => accessPerms.includes(p))) {
        if (permissions.includes('view_application')) {
          navigate('/lk', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      }
    }

    // На /lk/payments нужно право на платежи
    if (path === '/lk/payments') {
      const payPerms = ['view_payments', 'manage_payments'];
      if (!permissions.some(p => payPerms.includes(p))) {
        if (permissions.includes('view_application')) {
          navigate('/lk', { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      }
    }
  }, [account, loading, permissions, location.pathname]);

  if (loading) return <LoadingScreen />;
  return account && !needsClientProfile(account) ? children : null;
}

// ─── Основной контент ─────────────────────────────────────────────────────────
function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { account, loading, sessionError, restoreSession, logout, modalAuth, setModalAuth, showOrder, setShowOrder, editOrder, chatModal, setEditOrder, setChatModal, showQuickOrder, setShowQuickOrder, quickOrderCalcAmount } = useContext(LayoutContext);

  const guestMaterials = /^\/materials\/(?:folder\/)?[A-Za-z0-9_-]{32,128}\/?$/.test(location.pathname);

  // ── Логирование действий клиента: инициализация + просмотры страниц ──
  useEffect(() => { initTracker(); }, []);
  useEffect(() => {
    setTrackerEnabled(!guestMaterials);
    if (guestMaterials) { setTrackerPath('/materials/shared'); return; }
    setTrackerPath(location.pathname);
    track('client.page_view', { title: `Просмотр: ${location.pathname}`, path: location.pathname });
  }, [location.pathname, guestMaterials]);

  // ── Перехват реф. кода из URL ──
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');
    if (refCode) {
      // Сохраняем в localStorage — применим после регистрации
      localStorage.setItem('pending_referral_code', refCode.toUpperCase().trim());
      // Убираем ?ref= из URL, чтобы пользователь не видел его
      params.delete('ref');
      const newSearch = params.toString();
      const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '');
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  // ── Применить отложенный код после регистрации/входа ──
  useEffect(() => {
    if (!account) return;
    const pendingCode = localStorage.getItem('pending_referral_code');
    if (!pendingCode) return;
    
    import('./api').then(({ APIReferralApplyCode }) => {
      APIReferralApplyCode(pendingCode).then(res => {
        if (res?.status === 'ok') {
          console.log('Реф. код применён:', pendingCode);
          // Можно показать toast «Скидка действует на следующую заявку»
        }
        // Удаляем в любом случае — повторно не применяем
        localStorage.removeItem('pending_referral_code');
      });
    });
  }, [account]);

  // После входа возвращаемся в запрошенную папку, обычный вход ведёт в /lk.
  useEffect(() => {
    const handleLogin = () => {
      // Login and first profile completion stay on the customer materials link.
      if (/^\/materials\/(?:folder\/)?[A-Za-z0-9_-]{32,128}\/?$/.test(location.pathname)) {
        try { sessionStorage.removeItem(mediaReturnKey); } catch { /* приватный режим */ }
        return;
      }
      let target;
      try { target = sessionStorage.getItem(mediaReturnKey); sessionStorage.removeItem(mediaReturnKey); } catch { /* приватный режим */ }
      navigate(isMediaRoute(target) ? target : '/lk', {replace: true});
    };
    window.addEventListener("client_login_success", handleLogin);
    return () => window.removeEventListener("client_login_success", handleLogin);
  }, [navigate, location.pathname]);

  // Редирект после выхода → /
  useEffect(() => {
    const handleLogout = () => { if (!guestMaterials) navigate("/", { replace: true }); };
    window.addEventListener("client_logout", handleLogout);
    return () => window.removeEventListener("client_logout", handleLogout);
  }, [navigate, guestMaterials]);

  if (loading) return <LoadingScreen />;
  if (sessionError) return (
    <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ minHeight: '100vh', p: 3 }}>
      <Typography role="alert">{sessionError}</Typography>
      <Button onClick={restoreSession}>Повторить загрузку</Button>
      <Button variant="plain" onClick={logout}>Выйти из аккаунта</Button>
    </Stack>
  );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.body", display: "flex", flexDirection: "column" }}>
      <Header onShowOrder={() => { setEditOrder(null); setShowOrder(true); }} />

      <Box sx={{ flex: 1 }}>
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            <Route path="/materials/:shareToken" element={<MaterialsAccessPage />} />
            <Route path="/materials/folder/:folderShareToken" element={<MaterialsAccessPage audience="share" />} />
            {/* "/" — лендинг ВСЕГДА, даже для авторизованных */}
            <Route path="/" element={
              <motion.div
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -14 }}
                transition={{ duration: 0.32, ease: [0.4, 0, 0.2, 1] }}
              >
                <MainPage onShowOrder={() => { setEditOrder(null); setShowOrder(true); }} />
              </motion.div>
            } />

            {/* "/lk" — личный кабинет */}
            <Route path="/lk" element={
              <PrivateRoute>
                <LKPage onShowOrder={() => { setEditOrder(null); setShowOrder(true); }} />
              </PrivateRoute>
            }
            >
              {/* Вложенные роуты личного кабинета */}
              <Route path="" element={<HomePage />} />
              <Route path="access" element={<AccessPage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="profile" element={<ProfilePage />} />
              <Route path="referral" element={<ReferralPage />} />
              <Route path="payments" element={<PaymentsPage />} />
              <Route path="documents" element={<DocumentsPage />} />
              <Route path="storage/:objectId" element={<HomeMediaFolder audience="client" />} />
              <Route path="storage/:objectId/:section" element={<HomeMediaFolder audience="client" />} /> {/* HOME_MEDIA_V2_ROUTE */}
            </Route>

            {/* 404 → на главную */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AnimatePresence>
      </Box>

      {!account && <Footer />}

      {!guestMaterials && <AuthModal open={modalAuth} onClose={() => setModalAuth(false)} />}
      <OrderModal 
        open={showOrder} 
        onClose={() => setShowOrder(false)}
        editOrder={editOrder}
      />

      <ClientChatModal
        open={chatModal.open}
        chatId={chatModal.chatId}
        title={chatModal.title}
        applicationId={chatModal.applicationId}
        onClose={() => setChatModal({ open: false, chatId: null, title: '', applicationId: null })}
      />

      <QuickOrderModal
        open={showQuickOrder}
        onClose={() => setShowQuickOrder(false)}
        calculatorAmount={quickOrderCalcAmount}
      />

      {account && <PushPrompt />}
      {account && <NewsStoriesModal />}
    </Box>
  );
}

// ─── App ──────────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <CssVarsProvider theme={theme} defaultMode="light">
      <BrowserRouter>
        <LayoutContextProvider>
          <AppContent />
        </LayoutContextProvider>
      </BrowserRouter>
    </CssVarsProvider>
  );
}
